import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { forwardRef, useMemo } from 'react';
import styles from './setup-fee-and-vat.module.scss.js';
import { clsx } from '../../external/clsx/dist/clsx.js';
import { Typography, ErrorBoundary } from '@vp/swan';
import { useLocalization } from '../../hooks/useLocalizations.js';
import { VatMessageInternal, ShippingMessage } from '../vat-message/vat-message.component.js';
import { disabledLocalesForVat } from '../../constants/locale.js';
import { PriceInternal } from '../price/price.component.js';

const SetupFeeAndVatInternal = forwardRef((props, ref) => {
  const {
    className,
    setupFee,
    discountedSetupFee,
    vatInc = false,
    shippingIncl,
    hideShippingMessageInVatMessage = false,
    locale,
    currency,
    minimumFractionDigits,
    placeholdertext,
    ...rest
  } = props;
  const { t } = useLocalization(locale);
  const classNames = clsx(className, styles.setupFeeAndVat);
  const hideVatMessage = disabledLocalesForVat.some((unitLocale) => unitLocale.toLowerCase() === locale.toLowerCase());
  const message = useMemo(() => {
    if (!setupFee) return "noSetupFee";
    if (hideVatMessage) return "vatAndSetupFee.noVatInclSetup";
    if (hideShippingMessageInVatMessage && vatInc) return "vatAndSetupFee.inclVatAndSetup";
    if (hideShippingMessageInVatMessage && !vatInc) return "vatAndSetupFee.exclVatInclSetup";
    if (vatInc && shippingIncl) return "vatAndSetupFee.inclVatSetupInclShipping";
    if (vatInc && !shippingIncl) return "vatAndSetupFee.inclVatSetupExclShipping";
    if (!vatInc && shippingIncl) return "vatAndSetupFee.exclVatSetupInclShipping";
    return "vatAndSetupFee.exclVatSetupExclShipping";
  }, [hideShippingMessageInVatMessage, setupFee, shippingIncl, hideVatMessage, vatInc]);
  return /* @__PURE__ */ jsxs(Typography, { as: "span", fontSkin: "body-small", textColor: "subtle", ref, ...rest, className: classNames, children: [
    !setupFee && !hideVatMessage && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(VatMessageInternal, { vatAndShippingConfig: { vatInc, shippingIncl, hideShippingMessageInVatMessage }, locale, placeholdertext }),
      /* @__PURE__ */ jsx("br", {})
    ] }),
    t(message, {
      setupFee: setupFee && /* @__PURE__ */ jsx(
        PriceInternal,
        {
          placeholdertext,
          priceConfig: { vatInc, shippingIncl, minimumFractionDigits, locale, currency, listPrice: setupFee, discountedListPrice: discountedSetupFee }
        }
      ),
      shipping: /* @__PURE__ */ jsx(ShippingMessage, { locale })
    })
  ] });
});
const SetupFeeAndVAT = forwardRef((props, ref) => {
  return /* @__PURE__ */ jsx(
    ErrorBoundary,
    {
      fallback: /* @__PURE__ */ jsx(Typography, { as: "span", fontSkin: "body-small", textColor: "subtle", children: props.placeholdertext }),
      children: /* @__PURE__ */ jsx(SetupFeeAndVatInternal, { ref, ...props })
    }
  );
});

export { SetupFeeAndVAT, SetupFeeAndVatInternal };
